<template>
  <span class="text-sm leading-none">
    {{ t("total_savings", { totalAmount: toCash(portfolioInfo?.totalAmount || 0, NIS) }) }}
  </span>
  <div :class="cn('my-6 flex flex-col space-y-6', props.class)">
    <UiTabs
      v-if="isPending || debugStore.mockCategories || !noCategories"
      v-model="selectedCategoryModel"
      :list="portfolioCategories!"
      :loading="isPending"
      :swiper="{ enabled: isMobile }"
      :classes="{
        tabWrapper: '',
        tab: cn(
          'data-[selected=true]:border-risk5 data-[selected=true]:bg-risk5-foreground/35 data-[selected=true]:text-risk5 h-9 gap-1'
        ),
      }"
    >
      <template #default="item">
        {{ item.label }}
        <!-- <UiIconCircleSeparator />
        <span class="opacity-50">
          {{
            item.recommendationsCount === 0
              ? t("looking_good")
              : t("recommendations", { count: item.recommendationsCount })
          }}
        </span> -->
      </template>
    </UiTabs>
    <div class="flex h-6 items-center gap-1">
      <UiSkeleton
        :loading="isPending"
        :classes="{ line: 'w-32 rounded-full', container: 'w-auto' }"
        slot-tag="span"
      >
        {{ `${policy.totalAmount} ${pt(`categories.${selectedCategoryModel}`)}` }}
      </UiSkeleton>
      <UiSkeleton :loading="isPending" :classes="{ line: 'size-0.5 rounded-full', container: 'w-auto' }">
        <UiIconCircleSeparator />
      </UiSkeleton>
      <UiSkeleton :loading="isPending" :classes="{ line: 'w-16 rounded-full' }" slot-tag="span">
        {{ t(`total`, { total: toCash(policy?.totalSavings, NIS) }) }}
      </UiSkeleton>
    </div>
  </div>
</template>

<script setup lang="tsx">
import { sumBy } from "lodash-es"

import { MyPensionPortfolioPolicy, PensionCategoriesEnum } from "../../../types/portfolio"

const { isMobile } = useDisplay()
const props = defineProps<{
  class?: ClassValue
  policies: Nullable<MyPensionPortfolioPolicy[]>
}>()
const { t } = useI18n({ useScope: "local" })
const { t: pt } = useI18n({ useScope: "parent" })
const selectedCategoryModel = defineModel<PensionCategoriesEnum>({ default: PensionCategoriesEnum.PENSION })
const { debugStore } = usePensionPortfolioDebug()

const { data: portfolioInfo, isPending, isError } = useMyPortfolioInfo()

const noCategories = computed(
  () => (!isPending.value && !portfolioInfo.value?.categories?.length) || isError.value
)

const policy = computed(() => {
  return {
    totalSavings: props.policies?.length ? sumBy(props.policies, (policy) => policy.currentSavings) : 0,
    totalAmount: props.policies?.length || 0,
  }
})

const portfolioCategories = computed(() => {
  if (isPending.value) {
    return arrayInLength(5).map((_, index) => ({
      value: index,
      label: t("looking_good"),
      recommendationsCount: 0,
    }))
  }

  // if (!portfolioInfo.value?.categories) return []
  let categories = portfolioInfo.value?.categories || []
  if (debugStore.mockCategories) {
    // TODO)) Dev only mode to show mock data categories
    categories = [
      ...categories,
      ...Object.values(PensionCategoriesEnum)
        .filter((category) => !categories.find((c) => c.category === category))
        .map((category) => ({ category: category as PensionCategoriesEnum, recommendationsCount: 0 })),
    ]
  }

  return categories?.map(({ category, recommendationsCount }) => {
    return {
      value: category,
      label: pt(`categories.${category}`),
      recommendationsCount: recommendationsCount || 0,
    }
  })
})
</script>

<i18n lang="json">
{
  "he": {
    "categories": {
      "PENSION": "קרן פנסיה",
      "EDUCATION": "קרן השתלמות",
      "INVESTMENT": "גמל להשקעה",
      "MANAGERS": "ביטוח מנהלים",
      "CHILDSAVING": "חיסכון לכל ילד",
      "PROVIDENT": "קופת תגמולים"
    },
    "total_savings": "סה״כ חסכון שצברת עד כה {totalAmount}",
    "total": "סה״כ {total}",
    "looking_good": "נראה טוב",
    "recommendations": "{count} המלצה | {count} המלצות"
  },
  "en": {}
}
</i18n>
